import React, { useState } from "react";
import {
  ChevronDownIcon,
  ChevronUpIcon,
  CheckIcon,
  CommentIcon,
  CommentComposer,
  CommentList,
  DiscussIcon,
  StatusPill,
} from "../../StrategyCanvas/StrategyCanvasHelpers";
import useAnnualOkrReviewStore from "./_redux/AnnualOkrReviewStore";
import ObjectiveDetails from "../_shared/ObjectiveDetails";
import WigLinkChip from "../_shared/WigLinkChip";
import DimensionPill from "../_shared/DimensionPill";

// ═══════════════════════════════════════════════════════════════════════════
// One Objective in the coach's review — the Annual OKR counterpart of EXW's
// `ItemRow`.
//
// ⚠️ IT USES EXW'S OWN CLASS NAMES (`rr-row`, `rr-row-header`, `rr-actions`,
// `rr-action-btn rr-accept` / `rr-discuss`, `rr-comment-link`), NOT a local
// set. That is the point: an earlier pass invented `aokr-status-btn` and ended
// up with buttons in the wrong place and the wrong colours. These classes come
// from `StrategyCanvasReviewMode.scss`, which the parent imports, so the accept
// and to-discuss buttons are pixel-identical to the blueprint's by
// construction rather than by matching hex values by eye.
//
// ⚠️ NO EDIT ON COMMENTS. §6.5 has no PUT for Annual OKR comments, unlike
// Canvas's. `CommentList`/`CommentItem` already handle this — pass no `onEdit`
// and the row renders delete without edit. Do not fake an edit with a
// delete-and-recreate pair; it would change the comment's id and timestamp
// behind the coach's back.
//
// ⚠️ Status is freely re-settable (§6.4 is an upsert), unlike the Live Review's
// one-directional discuss-marking. Both buttons stay live after a choice, and
// clicking the active one is a no-op rather than a toggle-off: §6.4 has no
// "clear" value and the submit gate counts a non-null status.
// ═══════════════════════════════════════════════════════════════════════════

const ObjectiveRow = ({
  uniqueId,
  objective,
  expanded,
  onToggleExpand,
  readOnly,
  pending,
}) => {
  const [savingStatus, setSavingStatus] = useState(false);
  const [savingComment, setSavingComment] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [isCommenting, setIsCommenting] = useState(false);
  const [draft, setDraft] = useState("");

  // See the note beside the owner line below: §6.3 carries no `owner_name`
  // today, so this reads whichever of the plausible shapes the server starts
  // sending and renders nothing until one of them appears.
  const ownerName = objective.owner_name || objective.owner?.name || null;

  const status = objective.review_status;
  const comments = objective.comments || [];
  const id = objective.annual_objective_id;

  const handleSetStatus = async (next) => {
    if (savingStatus) return;
    setSavingStatus(true);
    await useAnnualOkrReviewStore
      .getState()
      .setObjectiveStatus(uniqueId, id, next);
    setSavingStatus(false);
  };

  const handleSaveComment = async () => {
    const text = draft.trim();
    if (!text || savingComment) return;
    setSavingComment(true);
    try {
      await useAnnualOkrReviewStore
        .getState()
        .addComment(uniqueId, {
          scopeType: "objective",
          scopeKey: id,
          text,
        });
      setDraft("");
      setIsCommenting(false);
    } catch {
      // The store already surfaced the error; the draft stays so the coach
      // doesn't lose what they typed.
    } finally {
      setSavingComment(false);
    }
  };

  const handleDeleteComment = async (commentId) => {
    setDeletingId(commentId);
    await useAnnualOkrReviewStore.getState().deleteComment(uniqueId, commentId);
    setDeletingId(null);
  };

  return (
    <div
      className={["rr-row", status ? `rr-${status}` : "", pending ? "rr-row--pending" : ""]
        .filter(Boolean)
        .join(" ")}
      // Read by the scroll-to-first-undecided behaviour in the parent, the same
      // way EXW's rows are.
      data-review-target={`objective-${id}`}
    >
      <button className="rr-row-header" onClick={onToggleExpand}>
        <span className="rr-row-left">
          <span className="rr-row-label">
            {objective.objective_statement || "Untitled Objective"}
          </span>
        </span>
        <span className="rr-row-right">
          {/* §6.3 carries `wig_label` on every Objective. Inside the header
              <button>, so not a focus stop of its own. */}
          {/* The Objective's Blueprint dimension, BEFORE the WIG link (by
              instruction, 2026-10-09): a fixed-width pill first keeps the link
              icons in one column down the list. Nothing when the payload
              doesn't carry `dimension`. */}
          <DimensionPill dimension={objective.dimension} />
          <WigLinkChip wigLabel={objective.wig_label} focusable={false} />
          {status && (
            <StatusPill
              accepted={status === "accepted"}
              className="rr-status-pill"
              acceptedClassName="rr-status-accepted"
              discussClassName="rr-status-to_discuss"
            />
          )}
          <span className="rr-chevron">
            {expanded ? <ChevronUpIcon /> : <ChevronDownIcon />}
          </span>
        </span>
      </button>

      {expanded && (
        <div className="rr-row-body">
          {/* The Key Results are the substance of an Objective — what the coach
              is actually judging — so they sit where EXW puts the item's value,
              directly under the header and above the decision. */}
          {/* ⚠️ THE SHARED BLOCK, not a local one. The same two facts are shown
              on the CEO's coach-remarks panel and on both sides of the live
              review; they were four different layouts until 2026-09-24. See
              `_shared/ObjectiveDetails.js`.

              ⚠️ `ownerUserId` IS PASSED NOW (JK copy review O21: the owner on
              every objective). It used to be withheld because the shared block
              printed "User #41"; it never prints the id any more, so passing it
              makes the owner line say "Owner name not available" instead of
              vanishing. §6.3 still has no `owner_name` (flagged to the backend
              2026-09-24) — the moment it arrives, the real name renders here
              with no further change. */}
          <ObjectiveDetails
            keyResults={objective.key_results}
            ownerName={ownerName}
            ownerUserId={objective.owner_user_id}
          />

          {!readOnly && (
            <div className="rr-actions">
              <button
                className={`rr-action-btn rr-accept ${status === "accepted" ? "active" : ""}`}
                onClick={() => handleSetStatus("accepted")}
                disabled={savingStatus}
              >
                <CheckIcon size={12} />
                Accept
              </button>
              <button
                className={`rr-action-btn rr-discuss ${status === "to_discuss" ? "active" : ""}`}
                onClick={() => handleSetStatus("to_discuss")}
                disabled={savingStatus}
              >
                <DiscussIcon size={12} />
                To discuss with the CEO
              </button>
            </div>
          )}

          {!readOnly && comments.length === 0 && (
            <button
              className="rr-comment-link"
              onClick={() => setIsCommenting((prev) => !prev)}
            >
              <CommentIcon size={12} />
              Add a comment for the CEO
            </button>
          )}

          {comments.length > 0 && (
            <CommentList
              comments={comments}
              onDelete={handleDeleteComment}
              deletingId={deletingId}
              readOnly={readOnly}
            />
          )}

          {!readOnly && isCommenting && (
            <CommentComposer
              className="rr-comment-composer"
              value={draft}
              onChange={setDraft}
              onSave={handleSaveComment}
              saving={savingComment}
              placeholder="Add context, a question, or suggested revision"
            />
          )}
        </div>
      )}
    </div>
  );
};

export default ObjectiveRow;
